import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Trophy,
  ArrowRight,
  Check,
  Swords,
  Target,
  Crosshair,
  ChevronDown,
  Star,
  Users,
  Award,
  Shield,
} from "lucide-react";
import { useRef } from "react";
import logo from "@/assets/ithopu-logo.png";
import iTHOPULogo from "@/assets/iTHOPU.png";
import landingVideo from "@/assets/Landing_Page_Video_Suggestion.mp4";
import backgroundImage from "@/assets/background-image.png";
import InteractiveDemoWidget from "@/components/landing/InteractiveDemoWidget";
import UserJourneySelector from "@/components/landing/UserJourneySelector";
import MobileStickyCTA from "@/components/landing/MobileStickyCTA";
import TestimonialsCarousel from "@/components/landing/TestimonialsCarousel";

export default function Landing() {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideoPlayback = (shouldPlay: boolean) => {
    if (videoRef.current) {
      if (shouldPlay) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  const handleLaunchApp = () => {
    // Marketing site (ithopu.com) → redirect to app subdomain
    if (
      window.location.hostname === "ithopu.com" ||
      window.location.hostname === "www.ithopu.com"
    ) {
      window.location.href = "https://app.ithopu.com";
    }
    // App subdomain or localhost → go to dashboard (will redirect to login if not authenticated)
    else {
      window.location.href = "/dashboard";
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900 relative">
      {/* Background Image for entire page */}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-15 pointer-events-none"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      {/* Navigation */}
      <nav className="border-b border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <a href="/" className="flex items-center gap-3">
                <img src={logo} alt="Ithopu" className="w-10 h-10" />
                <img src={iTHOPULogo} alt="Ithopu" className="h-8" />
              </a>
              <div className="hidden md:flex items-center gap-6">
                <a
                  href="#how-it-works"
                  className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                >
                  How It Works
                </a>
                <a
                  href="/pricing"
                  className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                >
                  Pricing
                </a>
                <a
                  href="/blog"
                  className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                >
                  Blog
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={handleLaunchApp}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Interactive Demo First */}
      <section className="relative overflow-hidden">
        {/* Background Video */}
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        >
          <source src={landingVideo} type="video/mp4" />
        </video>
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-50/60 via-zinc-50/50 to-zinc-50/70 dark:from-zinc-900/70 dark:via-zinc-900/60 dark:to-zinc-900/80" />

        <div className="container mx-auto px-4 py-12 md:py-20 relative z-10">
          <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-block mb-4 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-sm font-medium">
              ⚡ Can Your Child Solve Math in 2 Seconds?
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-zinc-900 dark:text-white mb-6 leading-tight">
              Become a Mental Math Champion in{" "}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
                21 Days
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-600 dark:text-zinc-400 mb-6 max-w-2xl mx-auto">
              Master Vedic & Russian math techniques through interactive practice
            </p>

            {/* Social Proof Badges */}
            <div className="flex items-center justify-center gap-6 mb-8 flex-wrap">
              <div className="flex items-center gap-2 bg-white dark:bg-zinc-800 px-4 py-2 rounded-full shadow-md border border-zinc-200 dark:border-zinc-700">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 ml-1">
                  4.9/5 from parents
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white dark:bg-zinc-800 px-4 py-2 rounded-full shadow-md border border-zinc-200 dark:border-zinc-700">
                <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Trusted by 1,000+ families
                </span>
              </div>
            </div>
          </div>

          {/* Interactive Demo Widget */}
          <InteractiveDemoWidget onVideoPlayback={handleVideoPlayback} />

          <div className="mt-6 flex items-center justify-center gap-8 text-sm text-zinc-600 dark:text-zinc-400">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span>No Credit Card Required</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span>Start in 30 Seconds</span>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Two Paths Section - Parent vs Teacher */}
      <section className="container mx-auto px-4 py-20 bg-white dark:bg-zinc-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-900 dark:text-white mb-4">
              Choose Your Path
            </h2>
            <p className="text-xl text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
              Whether you're a parent helping your child or a teacher managing a
              classroom, we've got you covered.
            </p>
          </div>

          <UserJourneySelector />
        </div>
      </section>

      {/* How It Works - Progressive Disclosure */}
      <section
        id="how-it-works"
        className="container mx-auto px-4 py-20 bg-zinc-50 dark:bg-zinc-900"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-900 dark:text-white mb-4">
              How Ithopu Works
            </h2>
            <p className="text-xl text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
              A complete system designed for mastery, not just memorization.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-2 border-blue-200 dark:border-blue-800 hover:shadow-xl transition-all bg-white dark:bg-zinc-800">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg flex items-center justify-center mb-4">
                  <Crosshair className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-zinc-900 dark:text-white">
                  1. Learn Techniques
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400">
                  Watch video tutorials and step-by-step guides for 90+ mental
                  math techniques.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 dark:border-purple-800 hover:shadow-xl transition-all bg-white dark:bg-zinc-800">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-zinc-900 dark:text-white">
                  2. Practice Daily
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400">
                  Drill with adaptive problems that match your skill level—15
                  minutes a day builds mastery.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200 dark:border-orange-800 hover:shadow-xl transition-all bg-white dark:bg-zinc-800">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 rounded-lg flex items-center justify-center mb-4">
                  <Swords className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-zinc-900 dark:text-white">
                  3. Compete
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400">
                  Battle friends, join tournaments, and climb leaderboards to
                  stay motivated.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-200 dark:border-green-800 hover:shadow-xl transition-all bg-white dark:bg-zinc-800">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 rounded-lg flex items-center justify-center mb-4">
                  <Trophy className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-zinc-900 dark:text-white">
                  4. Master It
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400">
                  Track progress, earn badges, and watch your speed transform in
                  just 3 weeks.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Certification Section */}
      <section className="container mx-auto px-4 py-20 bg-white dark:bg-zinc-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-900 dark:text-white mb-4">
              Earn Verifiable Certificates
            </h2>
            <p className="text-xl text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
              Prove your mastery with certificates you can share. Each one comes with a unique, publicly verifiable certificate number.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-blue-200 dark:border-blue-800 hover:shadow-xl transition-all bg-white dark:bg-zinc-800">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg flex items-center justify-center mb-4">
                  <Award className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-zinc-900 dark:text-white">
                  Technique Certificates
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 mb-3">
                  Master individual techniques and prove it. Pass a 20-problem exam with 90% accuracy to earn your certificate.
                </p>
                <p className="text-sm text-zinc-500 dark:text-zinc-500">
                  20 problems &middot; 90% to pass
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 dark:border-purple-800 hover:shadow-xl transition-all bg-white dark:bg-zinc-800">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-zinc-900 dark:text-white">
                  Category Certificates
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 mb-3">
                  Prove expertise across a full category like Vedic Math or Russian Math with a comprehensive 30-problem exam.
                </p>
                <p className="text-sm text-zinc-500 dark:text-zinc-500">
                  30 problems &middot; 85% to pass
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-200 dark:border-amber-800 hover:shadow-xl transition-all bg-white dark:bg-zinc-800">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 rounded-lg flex items-center justify-center mb-4">
                  <Trophy className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-zinc-900 dark:text-white">
                  Platform Certifications
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 mb-3">
                  Progress through 4 levels—Foundations, Intermediate, Advanced, and Master—to become a certified mental math expert.
                </p>
                <p className="text-sm text-zinc-500 dark:text-zinc-500">
                  4 levels &middot; Publicly verifiable
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof Section - Animated Testimonials Carousel */}
      <section className="container mx-auto px-4 py-20 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-zinc-900 dark:text-white mb-4">
            Results Parents & Teachers Love
          </h2>
          <div className="flex items-center justify-center gap-1 mb-12">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            <span className="text-lg font-semibold text-zinc-700 dark:text-zinc-300 ml-2">
              4.9/5 from 500+ families & educators
            </span>
          </div>
          <TestimonialsCarousel />
        </div>
      </section>

      {/* FAQ Section - Simplified */}
      <section className="container mx-auto px-4 py-20 bg-white dark:bg-zinc-900">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-900 dark:text-white mb-4">
              Common Questions
            </h2>
          </div>

          <div className="space-y-4">
            <details className="group bg-zinc-50 dark:bg-zinc-800 rounded-lg p-5 border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>How long until I see results?</span>
                <ChevronDown className="w-5 h-5 transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400" />
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-3">
                Most students see noticeable improvement in 2-3 weeks with 15-20
                minutes of daily practice.
              </p>
            </details>

            <details className="group bg-zinc-50 dark:bg-zinc-800 rounded-lg p-5 border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>Is it really free to start?</span>
                <ChevronDown className="w-5 h-5 transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400" />
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-3">
                Yes! No credit card required. Access fundamental techniques and
                practice problems for free. Upgrade anytime for advanced features.
              </p>
            </details>

            <details className="group bg-zinc-50 dark:bg-zinc-800 rounded-lg p-5 border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>What ages is this for?</span>
                <ChevronDown className="w-5 h-5 transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400" />
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-3">
                Designed for ages 8-16, but learners of all ages benefit. Our
                adaptive curriculum adjusts to any skill level.
              </p>
            </details>

            <details className="group bg-zinc-50 dark:bg-zinc-800 rounded-lg p-5 border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>Can parents monitor progress?</span>
                <ChevronDown className="w-5 h-5 transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400" />
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-3">
                Absolutely! The Parent Portal shows practice time, mastery levels,
                streaks, and weekly progress reports.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20 bg-zinc-50 dark:bg-zinc-900">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 rounded-3xl p-12 md:p-16 text-center shadow-2xl">
          <div className="text-6xl mb-6">🚀</div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Start Your 21-Day Transformation Today
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4">
            <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
            <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
            <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
            <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
            <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
            <span className="text-blue-50 font-semibold ml-2">
              Rated 4.9/5 by 500+ parents & teachers
            </span>
          </div>
          <p className="text-xl mb-8 text-blue-50 max-w-2xl mx-auto">
            Join 1,000+ students already mastering mental math. No credit
            card required—start practicing in 30 seconds.
          </p>
          <Button
            size="lg"
            onClick={handleLaunchApp}
            variant="secondary"
            className="text-lg px-10 py-7 bg-white text-zinc-900 hover:bg-zinc-100 font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-105"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <div className="mt-6 flex items-center justify-center gap-6 text-sm text-blue-50">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5" />
              <span>Free Forever Plan</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5" />
              <span>90+ Techniques</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5" />
              <span>Instant Access</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={logo} alt="Ithopu" className="w-8 h-8" />
                <div className="flex flex-col">
                  <span className="text-xl font-bold text-zinc-900 dark:text-white">
                    Ithopu
                  </span>
                  <span className="text-xs text-zinc-500 dark:text-zinc-400 -mt-1">
                    I am expert
                  </span>
                </div>
              </div>
              <p className="text-zinc-600 dark:text-zinc-400 text-sm">
                Transform into a mental math expert with ancient Vedic & Russian
                techniques through gamified practice.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-zinc-900 dark:text-white mb-4">
                Product
              </h4>
              <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
                <li>
                  <a
                    href="#features"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="/pricing"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <a
                    href="/roadmap"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Techniques
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-zinc-900 dark:text-white mb-4">
                Resources
              </h4>
              <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
                <li>
                  <a
                    href="/blog"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Blog
                  </a>
                </li>
                <li>
                  <a
                    href="/blog"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Tutorials
                  </a>
                </li>
                <li>
                  <a
                    href="/contact"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Support
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-zinc-900 dark:text-white mb-4">
                Company
              </h4>
              <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
                <li>
                  <a
                    href="/contact"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    About
                  </a>
                </li>
                <li>
                  <a
                    href="/contact"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Contact
                  </a>
                </li>
                <li>
                  <a
                    href="/beta-testing"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Join Beta
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Privacy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-zinc-200 dark:border-zinc-800 mt-8 pt-8 text-center text-sm text-zinc-600 dark:text-zinc-400">
            <p>&copy; 2026 Ithopu. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky Bottom CTA */}
      <MobileStickyCTA onLaunchApp={handleLaunchApp} />
    </div>
  );
}
